/**
 * CustomerSegmentPage — /analytics/template/customer-segment/:eventId
 *
 * Loyalty-tier breakdown (new / returning / frequent / diamond) + top
 * frequent guests + avg spend per segment + plan-by-segment.
 * PII-stripped: hashed band-IDs only; never raw customer names.
 *
 * V2.5-B Fork (2026-04-20). Critique round-1 HIGH #3: heatmap matrix
 * dropped because neither /extras.top_attractions (event-level) nor
 * /drilldown (per-band) supports a ride × tier matrix.
 *
 * Data routing:
 *   - /stats (one-shot)     — event meta
 *   - /drilldown (first 100, sort=scans DESC) — per-band rows
 *   - /customer-segments  (deferred) → derived from drilldown rows
 *     via local tier cutoffs
 */

import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type {
  BarChart as BarChartPayload,
  DonutChart as DonutChartPayload,
} from '../../types/chart'
import { ChatChart } from '../../components/chat/ChatChart'
import {
  DeferredPane,
  TemplateAnalyticsShell,
  BannerNote,
} from '../../components/analytics/TemplateAnalyticsShell'
import { CompactScoreTile } from '../../components/analytics/CompactScoreTile'
import {
  useEventAnalytics,
  type DrilldownRow,
} from '../../components/analytics/useEventAnalytics'

type Tier = 'new' | 'returning' | 'frequent' | 'diamond'

const TIER_LABEL: Record<Tier, string> = {
  new: 'New',
  returning: 'Returning',
  frequent: 'Frequent',
  diamond: 'Diamond',
}

const MM_PRIMARY = '#5A78FF'
const MM_TERTIARY = '#BB6BD9'
const MM_GREEN = '#20B99D'
const INK_SOFT = '#8697a8'

const TIER_COLOR: Record<Tier, string> = {
  new: INK_SOFT,
  returning: MM_PRIMARY,
  frequent: MM_GREEN,
  diamond: MM_TERTIARY,
}

function tierFromScans(scans: number): Tier {
  if (scans >= 20) return 'diamond'
  if (scans >= 5) return 'frequent'
  if (scans >= 2) return 'returning'
  return 'new'
}

function parseEventId(raw: string | undefined): number | null {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n)

/**
 * Stable client-side hash for a band_id scoped to the event. NOT
 * cryptographic — just a short displayable identifier that doesn't
 * re-identify across events. Keeps exported CSV rows joinable without
 * leaking the raw band_id.
 */
function hashBand(eventId: number, bandId: number): string {
  let h = 0
  const key = `${eventId}:${bandId}`
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) | 0
  }
  return `Band-${Math.abs(h).toString(36).slice(0, 6)}`
}

export function CustomerSegmentPage() {
  const { setCurrentEventId } = useAuth()
  const params = useParams<{ eventId?: string }>()
  const eventId = parseEventId(params.eventId)

  useEffect(() => {
    if (eventId) setCurrentEventId(eventId)
  }, [eventId, setCurrentEventId])

  const { stats, statsError, drilldown, drilldownLoading, drilldownError } =
    useEventAnalytics({
      eventId,
      fetchExtras: false,
      fetchCompare: false,
      fetchDrilldown: { pageSize: 100 },
    })

  const segmented = useMemo(() => {
    const rows = drilldown?.rows ?? []
    const buckets: Record<Tier, DrilldownRow[]> = {
      new: [],
      returning: [],
      frequent: [],
      diamond: [],
    }
    for (const r of rows) {
      buckets[tierFromScans(r.scans || 0)].push(r)
    }
    return buckets
  }, [drilldown])

  const tierDonut = useMemo<DonutChartPayload | null>(() => {
    const total = (Object.keys(segmented) as Tier[]).reduce(
      (a, t) => a + segmented[t].length,
      0,
    )
    if (total === 0) return null
    return {
      type: 'donut',
      title: 'Loyalty tiers',
      centerLabel: `${fmtNum(total)} bands`,
      slices: (Object.keys(segmented) as Tier[])
        .filter((t) => segmented[t].length > 0)
        .map((t) => ({
          label: TIER_LABEL[t],
          value: segmented[t].length,
          color: TIER_COLOR[t],
        })),
    }
  }, [segmented])

  const topFrequentChart = useMemo<BarChartPayload | null>(() => {
    const rows = drilldown?.rows ?? []
    if (rows.length === 0 || eventId == null) return null
    const top = rows
      .slice()
      .sort((a, b) => (b.scans || 0) - (a.scans || 0))
      .slice(0, 10)
    if (top.length === 0 || top.every((r) => !r.scans)) return null
    return {
      type: 'bar',
      title: 'Top 10 frequent guests',
      orientation: 'horizontal',
      series: top.map((r) => ({
        label: hashBand(eventId, r.band_id),
        value: r.scans || 0,
      })),
      xLabel: 'Lifetime scans',
    }
  }, [drilldown, eventId])

  const planBySegmentChart = useMemo<BarChartPayload | null>(() => {
    const rows = drilldown?.rows ?? []
    if (rows.length === 0) return null
    // Count rows per (tier, plan_name) pair, then keep the top 8 plans
    // by row count. Tier contributes as series label for grouping in
    // the horizontal bar.
    const tally: Record<string, number> = {}
    for (const r of rows) {
      const t = tierFromScans(r.scans || 0)
      const plan = r.plan_name || 'Unknown'
      const key = `${plan}:::${t}`
      tally[key] = (tally[key] || 0) + 1
    }
    const planTotals: Record<string, number> = {}
    for (const key of Object.keys(tally)) {
      const plan = key.split(':::')[0]
      planTotals[plan] = (planTotals[plan] || 0) + tally[key]
    }
    const topPlans = Object.entries(planTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
    if (topPlans.length === 0) return null
    return {
      type: 'bar',
      title: 'Bands by plan',
      orientation: 'horizontal',
      series: topPlans.map(([plan, count]) => ({
        label: plan,
        value: count,
      })),
      xLabel: 'Band count',
    }
  }, [drilldown])

  const avgSpendByTier = useMemo(() => {
    const out: Record<Tier, number | null> = {
      new: null,
      returning: null,
      frequent: null,
      diamond: null,
    }
    for (const t of Object.keys(segmented) as Tier[]) {
      const rows = segmented[t]
      if (rows.length === 0) continue
      const sum = rows.reduce((a, r) => a + (r.total_spent || 0), 0)
      out[t] = sum / rows.length
    }
    return out
  }, [segmented])

  if (eventId == null) {
    return (
      <TemplateAnalyticsShell title="Customer segments">
        <div
          data-testid="customer-segment-empty"
          className="rounded-card border border-dashed border-hairline bg-surface-stripe p-8 text-center text-sm text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          Pick an event to see the loyalty tier breakdown and repeat-play
          patterns.
        </div>
      </TemplateAnalyticsShell>
    )
  }

  if (statsError && !stats) {
    return (
      <TemplateAnalyticsShell title="Customer segments" eventName={null}>
        <BannerNote tone="warning" data-testid="customer-segment-stats-error">
          Couldn&apos;t load event stats: {statsError}
        </BannerNote>
      </TemplateAnalyticsShell>
    )
  }

  return (
    <TemplateAnalyticsShell
      title="Customer segments"
      subtitle={
        stats
          ? `${stats.event.event_name} · ${stats.event.event_date}`
          : 'Loading…'
      }
      eventName={stats?.event.event_name ?? null}
      windowChip={stats ? stats.event.event_date : null}
      banner={
        <BannerNote data-testid="customer-segment-pii-note">
          All customer identifiers are hashed. Band-ID hashes are stable
          within this event only.
        </BannerNote>
      }
    >
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        data-testid="customer-segment-grid"
      >
        {/* 1. Loyalty tier donut */}
        {drilldownLoading ? (
          <DeferredPane title="Loyalty tiers" message="Loading segment data..." />
        ) : tierDonut ? (
          <ChatChart chart={tierDonut} density="canvas" />
        ) : (
          <DeferredPane
            title="Loyalty tiers"
            message={
              drilldownError
                ? "Per-band rows aren't available for this event."
                : 'No segmented customer rows available yet.'
            }
          />
        )}

        {/* 2. Top frequent guests */}
        {topFrequentChart ? (
          <ChatChart chart={topFrequentChart} density="canvas" />
        ) : (
          <DeferredPane
            title="Top frequent guests"
            message="No repeat-scanning bands logged yet."
          />
        )}

        {/* 3. Avg spend per segment — 4-tile row, full-width on md+ */}
        <section
          aria-label="Average spend per segment"
          className="grid grid-cols-2 gap-3 md:col-span-2 md:grid-cols-4"
          data-testid="customer-segment-spend-row"
        >
          {(Object.keys(TIER_LABEL) as Tier[]).map((t) => {
            const avg = avgSpendByTier[t]
            return (
              <CompactScoreTile
                key={t}
                label={`${TIER_LABEL[t]} avg spend`}
                // Map spend to a 0-100 bar against $200 reference; hide
                // bar when no data.
                scorePct={
                  avg === null ? null : Math.min(100, Math.round((avg / 200) * 100))
                }
                caption={avg === null ? undefined : fmtUSD(avg)}
                unavailableMessage="No bands in this tier yet"
                data-testid={`customer-segment-tile-${t}`}
              />
            )
          })}
        </section>

        {/* 4. Plan-by-segment */}
        {planBySegmentChart ? (
          <div className="md:col-span-2">
            <ChatChart chart={planBySegmentChart} density="canvas" />
          </div>
        ) : (
          <div className="md:col-span-2">
            <DeferredPane
              title="Plan × tier"
              message="Plan data isn\u2019t available for this event yet."
            />
          </div>
        )}

        {/* 5. Customer segment endpoint (deferred) — explicitly note to
            operators that a richer breakdown is coming. */}
        <div className="md:col-span-2">
          <DeferredPane
            title="Returning-customer lifetime lookup"
            message="Customer tier breakdown will show when segment data lands — today&apos;s view is derived from this event&apos;s scans only."
          />
        </div>
      </div>
    </TemplateAnalyticsShell>
  )
}
